import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { STRINGS } from "@/lib/i18n";
import { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogoMark } from "@/components/Logo";

export default function AuthModal({ open, onOpenChange }) {
  const { login, register } = useAuth();
  const { lang } = useLanguage();
  const t = STRINGS[lang].auth;
  const navigate = useNavigate();
  const [tab, setTab] = useState("entrar");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (tab === "entrar") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success(tab === "entrar" ? t.welcomeBack : t.accountCreated);
      onOpenChange(false);
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0B101C] sm:max-w-md" data-testid="auth-modal">
        <DialogHeader className="items-center text-center">
          <LogoMark className="h-12 w-12" />
          <DialogTitle className="font-serif text-2xl font-medium text-blue-50">{t.title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">{t.subtitle}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
          <TabsList className="grid w-full grid-cols-2 rounded-full border border-white/10 bg-[#05070D] p-1">
            <TabsTrigger
              value="entrar"
              data-testid="auth-tab-login"
              className="rounded-full font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-blue-400 data-[state=active]:text-[#05070D]"
            >
              {t.tabLogin}
            </TabsTrigger>
            <TabsTrigger
              value="criar"
              data-testid="auth-tab-register"
              className="rounded-full font-mono text-[11px] uppercase tracking-[0.15em] data-[state=active]:bg-blue-400 data-[state=active]:text-[#05070D]"
            >
              {t.tabRegister}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entrar">
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="login-form">
              <input type="email" required value={form.email} onChange={set("email")} placeholder={t.emailPh} data-testid="login-email-input" className={inputClass} />
              <input type="password" required value={form.password} onChange={set("password")} placeholder={t.passwordPh} data-testid="login-password-input" className={inputClass} />
              {error && <p className="text-xs text-red-400" data-testid="login-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-400 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t.loginBtn}
              </button>
            </form>
          </TabsContent>

          <TabsContent value="criar">
            <form onSubmit={submit} className="mt-5 space-y-4" data-testid="register-form">
              <input required value={form.name} onChange={set("name")} placeholder={t.namePh} data-testid="register-name-input" className={inputClass} />
              <input type="email" required value={form.email} onChange={set("email")} placeholder={t.emailPh} data-testid="register-email-input" className={inputClass} />
              <input type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder={t.passwordNewPh} data-testid="register-password-input" className={inputClass} />
              {error && <p className="text-xs text-red-400" data-testid="register-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="register-submit-button"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-400 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t.registerBtn}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
